import { Col, Row } from 'antd';
import { isFinite } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type StockPublicDataFragment } from '../../../../api/fragments/StockPublicData';
import breakpoints from '../../../../utilities/breakpoints';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import { useExtraDataContext } from './ExtraDataContext';

const AdditionalInfoContainer = styled.div`
    margin-bottom: 50px;
    opacity: 0.75;

    &:last-child {
        margin-bottom: 0;
    }
`;

const AdditionalInfoDetailContainer = styled.div`
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const AdditionalInfoTitle = styled.div`
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 20px;
`;

const AdditionalInfoSubtitle = styled.div`
    font-size: 16px;
    font-weight: bold;
`;

const AdditionalInfoDescription = styled.div`
    font-size: 16px;
`;

const VehicleDetailsContainer = styled.div`
    background-color: #eff0f1;
    padding: 16px;
    border-radius: 2px;
    margin-bottom: 50px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 16px 30px;
    }
`;

const Title = styled.div`
    font-size: 22px;
    font-weight: bold;
`;

const VehicleDetails = styled.div`
    font-size: 16px;
    padding: 8px 0;
    border-bottom: 0.5px solid #b8becb;

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    & .ant-row > .ant-col:last-child {
        font-weight: bold;
    }
`;

type DetailsProps = {
    label: string;
    value: string;
};

const SpecificationDetails = ({ label, value }: DetailsProps) => (
    <VehicleDetails>
        <Row>
            <Col lg={13} xs={12}>
                {label}
            </Col>
            <Col lg={11} xs={12}>
                {value || '-'}
            </Col>
        </Row>
    </VehicleDetails>
);

const TechnicalDetails = ({ label, value }: DetailsProps) => (
    <VehicleDetails>
        <Row>
            <Col span={15}>{label}</Col>
            <Col span={9}>{value || '-'}</Col>
        </Row>
    </VehicleDetails>
);

export type AdditionalInfoProps = {
    additionalInfo: Extract<StockPublicDataFragment, { __typename: 'MobilityStockInventory' }>['mobilities'][number];
};

const AdditionalInfoDetail = ({ subTitle, description }: { subTitle: string; description: string }) => (
    <AdditionalInfoDetailContainer>
        <AdditionalInfoSubtitle>{subTitle}</AdditionalInfoSubtitle>
        <AdditionalInfoDescription>{description}</AdditionalInfoDescription>
    </AdditionalInfoDetailContainer>
);

const AdditionalInfo = ({ additionalInfo }: AdditionalInfoProps) => {
    const translatedString = useTranslatedString();

    if (additionalInfo.__typename !== 'MobilityAdditionalInfo') {
        return null;
    }

    return (
        <AdditionalInfoContainer>
            <AdditionalInfoTitle>{translatedString(additionalInfo.title)}</AdditionalInfoTitle>
            {additionalInfo.detail.map((detail, index: number) => (
                <AdditionalInfoDetail
                    key={`additionalInfoDetail-${index.toString()}`}
                    description={translatedString(detail.description)}
                    subTitle={translatedString(detail.subTitle)}
                />
            ))}
        </AdditionalInfoContainer>
    );
};

export type BookingInformationsProps = {
    stock: Extract<StockPublicDataFragment, { __typename: 'MobilityStockInventory' }>;
};

const BookingInformations = ({ stock }: BookingInformationsProps) => {
    const { t } = useTranslation('mobilityVehicleDetails');

    const psPowerOutput = useMemo(() => {
        if (stock.inventory.__typename !== 'MobilityInventory' || !isFinite(stock.inventory.variant.powerOutput)) {
            return null;
        }

        return Math.round(stock.inventory.variant.powerOutput * 1.3596216173039);
    }, [stock]);

    const { application } = useExtraDataContext();

    const additionalInfos = useMemo(() => {
        if (application) {
            return application.mobilitySnapshots.map(
                snapshot => snapshot.__typename === 'MobilityAdditionalInfoSnapshot'
            );
        }

        return stock.mobilities
            .filter(mobility => mobility.__typename === 'MobilityAdditionalInfo')
            .toSorted((a, b) => a.order - b.order);
    }, [stock.mobilities, application]);

    if (stock.inventory.__typename !== 'MobilityInventory') {
        return null;
    }

    return (
        <>
            <VehicleDetailsContainer>
                <Row gutter={[36, 40]} justify="start">
                    <Col lg={12} xl={8} xs={24}>
                        <Title>{t('mobilityVehicleDetails:vehicleDetails.titles.specifications')}</Title>
                        <SpecificationDetails
                            label={t('mobilityVehicleDetails:vehicleDetails.labels.modelYear')}
                            value={
                                stock.inventory.variant.modelYear ? stock.inventory.variant.modelYear.toString() : null
                            }
                        />
                        <SpecificationDetails
                            label={t('mobilityVehicleDetails:vehicleDetails.labels.seats')}
                            value={stock.inventory.variant.seats?.toString()}
                        />
                        <SpecificationDetails
                            label={t('mobilityVehicleDetails:vehicleDetails.labels.transmission')}
                            value={stock.inventory.variant.transmission}
                        />
                        <SpecificationDetails
                            label={t('mobilityVehicleDetails:vehicleDetails.labels.appleCarPlay')}
                            value={t(
                                `mobilityVehicleDetails:vehicleDetails.${
                                    stock.inventory.variant.appleCarPlay ? 'available' : 'unavailable'
                                }`
                            )}
                        />
                    </Col>
                    <Col lg={0} xl={1} xs={0} />
                    <Col lg={12} xl={12} xs={24}>
                        <Title>{t('mobilityVehicleDetails:vehicleDetails.titles.technicalData')}</Title>
                        <TechnicalDetails
                            label={t('mobilityVehicleDetails:vehicleDetails.labels.powser')}
                            value={t('mobilityVehicleDetails:vehicleDetails.powerOutput', {
                                kwValue: stock.inventory.variant.powerOutput,
                                psValue: psPowerOutput,
                            })}
                        />
                        <TechnicalDetails
                            label={t('mobilityVehicleDetails:vehicleDetails.labels.acceleration')}
                            value={t('mobilityVehicleDetails:vehicleDetails.acceleration', {
                                value: stock.inventory.variant.topSpeed,
                            })}
                        />
                    </Col>
                </Row>
            </VehicleDetailsContainer>
            {additionalInfos.map((additionalInfo, index: number) => (
                <AdditionalInfo key={`additionalInfo-${index.toString()}`} additionalInfo={additionalInfo} />
            ))}
        </>
    );
};

export default BookingInformations;
